import React, { useCallback, useState, useRef } from 'react';
import './Table.css';
import TableInfo from '../../Screens/TableInfo/TableInfo';
import { useReservations } from '../../api/reservations';
import { createPortal } from 'react-dom'; // Import createPortal

const Table = ({
  table,
  displayNumber,
  onDragStart,
  onTableClick,
  isEditing,
  selectedReservation,
  onRotate,
  onDelete,
  selectedDate,
  tempSelectedTables,
  onDragEnd,
}) => {
  const { data: reservations = [], isLoading, error } = useReservations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartTimeRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const isTouchMovedRef = useRef(false);

  const getChairPositions = useCallback(() => {
    const positions = [];
    const chairWidth = 20;
    const chairHeight = 10;

    if (table.shape === 'circle') {
      const radius = table.width / 2;
      const angleStep = (2 * Math.PI) / table.seats;
      for (let i = 0; i < table.seats; i++) {
        const angle = i * angleStep;
        const x = radius + (radius + chairHeight) * Math.cos(angle) - chairWidth / 2;
        const y = radius + (radius + chairHeight) * Math.sin(angle) - chairHeight / 2;
        positions.push({
          x,
          y,
          angle: angle + Math.PI / 2,
          filled: false,
        });
      }
    } else if (table.shape === 'rectangle') {
      const seatsPerLongSide = Math.max(1, Math.floor((table.seats - 2) / 2));
      const totalLongSideSeats = seatsPerLongSide * 2;
      const spacingX = table.width / (seatsPerLongSide + 1);

      for (let i = 0; i < seatsPerLongSide; i++) {
        positions.push({
          x: (i + 1) * spacingX - chairWidth / 2,
          y: -chairHeight,
          angle: 0,
          filled: false,
        });
      }

      for (let i = 0; i < seatsPerLongSide; i++) {
        positions.push({
          x: (i + 1) * spacingX - chairWidth / 2,
          y: table.height,
          angle: Math.PI,
          filled: false,
        });
      }

      if (totalLongSideSeats < table.seats) {
        positions.push({
          x: -chairHeight - 5,
          y: table.height / 2 - chairWidth / 2,
          angle: -Math.PI / 2,
          filled: false,
        });
      }

      if (totalLongSideSeats + 1 < table.seats) {
        positions.push({
          x: table.width - 5,
          y: table.height / 2 - chairWidth / 2,
          angle: Math.PI / 2,
          filled: false,
        });
      }
    } else if (table.shape === 'square') {
      const seatsPerSide = Math.ceil(table.seats / 4);
      const spacing = table.width / (seatsPerSide + 1);

      for (let i = 0; i < seatsPerSide && positions.length < table.seats; i++) {
        positions.push({
          x: (i + 1) * spacing - chairWidth / 2,
          y: -chairHeight,
          angle: 0,
          filled: false,
        });
      }
      for (let i = 0; i < seatsPerSide && positions.length < table.seats; i++) {
        positions.push({
          x: table.width,
          y: (i + 1) * spacing - chairWidth / 2,
          angle: Math.PI / 2,
          filled: false,
        });
      }
      for (let i = 0; i < seatsPerSide && positions.length < table.seats; i++) {
        positions.push({
          x: (i + 1) * spacing - chairWidth / 2,
          y: table.height,
          angle: Math.PI,
          filled: false,
        });
      }
      for (let i = 0; i < seatsPerSide && positions.length < table.seats; i++) {
        positions.push({
          x: -chairHeight-10,
          y: (i + 1) * spacing - chairWidth / 2,
          angle: -Math.PI / 2,
          filled: false,
        });
      }
    }

    const tableReservations = reservations.filter(res =>
      Array.isArray(res.tableIds) && res.tableIds.includes(table.id) && res.time.startsWith(selectedDate)
    );
    let filledChairs = 0;

    tableReservations.forEach(reservation => {
      const chairsToFill = Math.min(reservation.partySize, positions.length - filledChairs);
      for (let i = filledChairs; i < filledChairs + chairsToFill; i++) {
        if (i < positions.length) {
          positions[i].filled = true;
        }
      }
      filledChairs += chairsToFill;
    });

    return positions;
  }, [table, selectedDate, reservations]);

  if (isLoading) return <div>Loading table reservations...</div>;
  if (error) return <div>Error loading table reservations</div>;

  const chairPositions = getChairPositions();
  const tableReservations = reservations.filter(res =>
    Array.isArray(res.tableIds) && res.tableIds.includes(table.id) && res.time.startsWith(selectedDate)
  );
  const hasReservationOnDate = tableReservations.length > 0;
  const isSeated = tableReservations.some(res => res.status === 'seated');
  const isTempSelected = tempSelectedTables.includes(table.id);

  const hasTableConflict = () => {
    for (let i = 0; i < tableReservations.length; i++) {
      const res1 = tableReservations[i];
      const time1 = new Date(res1.time).getTime();
      for (let j = i + 1; j < tableReservations.length; j++) {
        const res2 = tableReservations[j];
        const time2 = new Date(res2.time).getTime();
        const timeDiff = Math.abs(time1 - time2);
        if (timeDiff <= 60 * 60 * 1000) {
          return true;
        }
      }
    }
    return false;
  };

  const isConflicted = hasTableConflict();

  const handleTableClick = () => {
    if (!isEditing && !selectedReservation) {
      setIsModalOpen(true);
    } else {
      onTableClick(table.id);
    }
  };

  const handleTouchStart = (e) => {
    if (isEditing) {
      setIsDragging(true);
      touchStartTimeRef.current = Date.now();
      const touch = e.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      isTouchMovedRef.current = false;
      onDragStart(e, table);
    } else {
      // Store touch start time for tap detection
      touchStartTimeRef.current = Date.now();
      const touch = e.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      isTouchMovedRef.current = false;
    }
  };

  const handleTouchMove = (e) => {
    if (isEditing) {
      isTouchMovedRef.current = true;
    } else {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);
      if (deltaX > 10 || deltaY > 10) {
        isTouchMovedRef.current = true;
      }
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    if (isDragging) {
      setIsDragging(false);
      if (onDragEnd) onDragEnd(table.id);
    }
    if (!isEditing && touchStartTimeRef.current) {
      const touchDuration = Date.now() - touchStartTimeRef.current;
      if (touchDuration < 300 && !isTouchMovedRef.current) {
        handleTableClick();
      }
    }
    touchStartTimeRef.current = null;
    touchStartPosRef.current = { x: 0, y: 0 };
    isTouchMovedRef.current = false;
  };

  const handleMouseDown = (e) => {
    if (isEditing) {
      setIsDragging(true);
      onDragStart(e, table);
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      setIsDragging(false);
      if (onDragEnd) onDragEnd(table.id);
    }
  };

  return (
    <>
      <div
        className={`table table-${table.shape}`}
        style={{
          left: `${table.x}px`,
          top: `${table.y}px`,
          width: `${table.width}px`,
          height: `${table.height}px`,
          transform: `rotate(${table.rotation}deg)`,
          transformOrigin: 'center',
          backgroundColor: isSeated
            ? '#90ee90'
            : hasReservationOnDate
            ? '#e0f7fa'
            : isTempSelected
            ? '#d3d3d3'
            : '#fff',
          border: isConflicted
            ? '2px solid red'
            : selectedReservation
            ? '2px dashed #ff0000'
            : '2px solid #666',
          position: 'absolute',
          zIndex: isDragging ? 10 : 1,
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleTableClick}
      >
        <span className="table-number">{displayNumber}</span>
        {chairPositions.map((pos, index) => (
          <div
            key={index}
            className={`chair ${pos.filled ? 'chair-filled' : ''}`}
            style={{
              left: `${pos.x}px`,
              top: `${pos.y}px`,
              transform: `rotate(${pos.angle}rad)`,
              position: 'absolute',
            }}
          />
        ))}
        {isEditing && (
          <>
            <button
              className="rotate-button"
              onClick={e => {
                e.stopPropagation();
                onRotate(table.id);
              }}
              onTouchStart={e => {
                e.stopPropagation();
                onRotate(table.id);
              }}
              onTouchEnd={e => e.stopPropagation()}
            >
              ↻
            </button>
            <button
              className="delete-button"
              onClick={e => {
                e.stopPropagation();
                onDelete(table.id);
              }}
              onTouchStart={e => {
                e.stopPropagation();
                onDelete(table.id);
              }}
              onTouchEnd={e => e.stopPropagation()}
            >
              ✕
            </button>
          </>
        )}
      </div>
      {isModalOpen &&
        createPortal(
          <TableInfo
            tableId={table.id}
            reservations={reservations}
            selectedDate={selectedDate}
            onClose={() => setIsModalOpen(false)}
          />,
          document.body // Render the modal directly under <body>
        )}
    </>
  );
};

export default Table;